// DOM icons (drag ghost, preview bubble, later shop cards): the M6 IconProvider contract (fx/icons.ts) with
// the real art. IMPLEMENTATION §2.9: drawn straight from the generators, never a toBlob of the atlas; a
// 64-entry LRU (palette.json art.domIconCache) of rendered frames keyed by frame id × backing-store size.
// `draw(key, target, px)` drawImages the cached frame, fitted into a px-CSS-px square (backing store
// px × dpr), INTO THE CALLER'S canvas (the ghost's / bubble's pooled one, resized + cleared here): a cached
// canvas is never handed out, inserted into the DOM or cleared by a caller. Canvases evicted from the LRU are freed (§2.9: iOS counts canvas memory until GC).
// Board and DOM show the same frame: the catalog spec the atlas bakes (items carry their tier badge), plus
// the overlays the board draws over a body (a drill: its head `bld.drill.head.<L>`, unturned = pointing N).
import type { IconKey, IconProvider } from '../fx/icons.ts';
import { artEnv, buildCatalog, frameId } from './catalog.ts';
import { type CanvasLike, contextOf, createCanvas, freeCanvas } from './ctx.ts';
import { drawFrame, frameSize } from './generators.ts';
import type { ArtDefs, ArtEnv, FrameSpec } from './types.ts';

/** The catalog frame an icon key shows (a chute: the straight W → E body). */
export function iconFrameId(key: IconKey): string {
  if (key.k === 'item') return frameId.item(key.fam, key.tier);
  if (key.type === 'chute') return frameId.chute('W', 'E');
  return frameId.building(key.type, key.ore, key.level);
}

/**
 * The overlay frames the board draws over that body (BuildingViews), in draw order, unturned: a drill's
 * head (drawn pointing N). Press / grinder / smelter bodies already include their moving part at rest.
 */
export function iconOverlayIds(key: IconKey): string[] {
  return key.k === 'building' && key.type === 'drill' ? [frameId.drillHead(key.level)] : [];
}

export interface DomIcons extends IconProvider {
  /** Any catalog frame by id (UI icons `ui.coin`, Sarge, …) into the caller's canvas; false if unknown. */
  drawFrame(id: string, target: HTMLCanvasElement, px: number): boolean;
  /** Cached frames (diagnostics / tests). */
  cacheSize(): number;
}

export interface DomIconsOptions {
  /** The current game data (DEV data reload swaps it: the cache is rebuilt). */
  defs(): ArtDefs;
  /** dprEff: backing-store px per CSS px. */
  dpr(): number;
}

export function createDomIcons(o: DomIconsOptions): DomIcons {
  let src: ArtDefs | null = null;
  let specs = new Map<string, FrameSpec>();
  let env: ArtEnv | null = null;
  let envDpr = 0;
  let cap = 0;
  const cache = new Map<string, CanvasLike>();

  const clear = (): void => {
    for (const c of cache.values()) freeCanvas(c);
    cache.clear();
  };
  const sync = (): void => {
    const d = o.defs();
    const dpr = o.dpr();
    if (d !== src) {
      src = d;
      specs = new Map(buildCatalog(d, { version: 'all' }).map((s) => [s.key, s]));
      cap = d.palette.art.domIconCache;
      env = null;
    }
    if (env === null || dpr !== envDpr) {
      clear();
      env = artEnv(d, dpr);
      envDpr = dpr;
    }
  };

  /**
   * The cached rendering of `spec` (+ its `overlays`, centred on it at the same cell scale) fitted into a
   * `box`-px square (LRU order refreshed).
   */
  const rendered = (spec: FrameSpec, overlays: readonly FrameSpec[], box: number): CanvasLike => {
    const id = `${[spec, ...overlays].map((s) => s.key).join('+')}@${box}`;
    const hit = cache.get(id);
    if (hit) {
      cache.delete(id);
      cache.set(id, hit);
      return hit;
    }
    const cellPx = box / Math.max(spec.w, spec.h);
    const [w, h] = frameSize(spec, cellPx);
    const c = createCanvas(w, h);
    const g = contextOf(c);
    drawFrame(g, spec, cellPx, env as ArtEnv);
    for (const o of overlays) {
      const [ow, oh] = frameSize(o, cellPx);
      g.save();
      g.translate((w - ow) / 2, (h - oh) / 2);
      drawFrame(g, o, cellPx, env as ArtEnv);
      g.restore();
    }
    cache.set(id, c);
    while (cache.size > cap) {
      const oldest = cache.keys().next().value;
      if (oldest === undefined) break;
      const old = cache.get(oldest);
      cache.delete(oldest);
      if (old) freeCanvas(old);
    }
    return c;
  };

  const drawInto = (
    id: string,
    target: HTMLCanvasElement,
    px: number,
    overlayIds: readonly string[] = [],
  ): boolean => {
    sync();
    const spec = specs.get(id) ?? specs.get(frameId.missing);
    if (!spec) return false;
    const overlays = spec.key === id ? overlayIds.flatMap((o) => specs.get(o) ?? []) : [];
    const box = Math.max(1, Math.round(px * o.dpr()));
    if (target.width !== box) target.width = box;
    if (target.height !== box) target.height = box;
    const g = target.getContext('2d');
    if (!g) return false;
    g.clearRect(0, 0, box, box);
    const img = rendered(spec, overlays, box);
    const { width: w, height: h } = img;
    g.drawImage(img as CanvasImageSource, 0, 0, w, h, (box - w) / 2, (box - h) / 2, w, h);
    return true;
  };

  return {
    draw: (key, target, px) => drawInto(iconFrameId(key), target, px, iconOverlayIds(key)),
    drawFrame: (id, target, px) => drawInto(id, target, px),
    cacheSize: () => cache.size,
  };
}
